/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useEffect, useState } from 'react';
import { TimeSeries, TimeRange } from 'pondjs';

import {
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  LineChart,
  Baseline,
  Legend,
  styler,
  ScatterChart,
  Resizable,
} from 'react-timeseries-charts';
import { makeStyles, FormControlLabel, Switch } from '@material-ui/core';

import TimeseriesChartTooltip from './TimeseriesChartTooltip';
import { round, roundAndExponent } from '../../utils/math';

const useStyles = makeStyles(() => ({
  iconsRow: {
    height: '39px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  legendRow: {
    height: '39px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    span: {
      height: '100%',
    },
  },
  chartContainer: {
    width: '100%',
  },
  thresholdInfo: {
    fontSize: 'smaller',
    color: 'rgb(153, 153, 153)',
    textAlign: 'center',
  },
  trackerDetailsInfo: {
    fontSize: 'smaller',
    color: 'rgb(0, 0, 0)',
    textAlign: 'center',
  },
}));

const validPointForLogScale = (point: number | null) => (point !== null && point <= 0 ? null : point);

const createLineChartData = (name: string, columns: string[], points: unknown[][], isLog: boolean) =>
  new TimeSeries({
    name,
    columns,
    points: !isLog
      ? points
      : points.map((point) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const newPoint = [...point];
          newPoint[1] = validPointForLogScale(newPoint[1] as number);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return newPoint;
        }),
  });

const getChartMinMax = (
  chartData: TimeSeries,
  timeRange: TimeRange,
  isLog: boolean,
  columns: string[]
): Record<string, number> => {
  const timeSeriesInView = typeof timeRange !== 'undefined' ? chartData.crop(timeRange) : chartData;
  let max =
    // eslint-disable-next-line no-nested-ternary
    columns.length <= 1
      ? timeSeriesInView.max(columns[1])
      : columns
          .slice(3, 5)
          .reduce((a, column) => Math.max(a, timeSeriesInView.max(column)), timeSeriesInView.max(columns[1]));
  let min =
    // eslint-disable-next-line no-nested-ternary
    columns.length <= 1
      ? timeSeriesInView.min(columns[1], (values: number[]) => values)
      : columns.slice(3, 5).reduce(
          (a, column) =>
            Math.min(
              a,
              timeSeriesInView.min(column, (values: number[]) => values)
            ),
          timeSeriesInView.min(columns[1], (values: number[]) => values)
        );

  if (typeof max === 'undefined') {
    max = 1000;
  }
  if (typeof min === 'undefined') {
    min = 0;
  }

  // Can't have 0 values in log scale
  if (isLog) {
    if (max <= 0) {
      max = 1000;
    }
    if (min <= 0) {
      min = 1;
    }
  }
  return { max, min };
};

const getTrackerDetails = (tracker: Date | undefined, chartData: TimeSeries | undefined) => {
  if (typeof tracker === 'undefined' || typeof chartData === 'undefined') {
    return [];
  }

  const index = chartData.bisect(tracker, 0);
  const columns = chartData.columns();
  let trackerValues: LabelAndValue[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const column of columns) {
    if (column === 'value') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = chartData.at(index).get(column);
      if (typeof value === 'undefined') {
        trackerValues = [];
        break;
      }
      // eslint-disable-next-line no-continue
      continue;
    }
    const labelAndValue: LabelAndValue = {
      label: column.charAt(0).toUpperCase().concat(column.slice(1)),
      value: roundAndExponent(chartData.at(index).get(column)),
    };
    trackerValues.push(labelAndValue);
  }
  return trackerValues;
};

const renderTracker = (columnDefs: string[], yLabel: string, trackerDetailsInfo: LabelAndValue[]) => {
  return (
    columnDefs.length > 2 &&
    (!trackerDetailsInfo ? (
      // Dummy text to reserve a line
      <span style={{ visibility: 'hidden' }}>a</span>
    ) : (
      <div>
        {yLabel && trackerDetailsInfo.length > 0 && (
          <span>
            <b>{yLabel.toUpperCase()}</b> -{' '}
          </span>
        )}
        {trackerDetailsInfo.map(({ label, value }) => (
          <span key={label}>
            <b>{label}:</b> {`${value} `}
          </span>
        ))}
      </div>
    ))
  );
};

export interface LabelAndValue {
  label: string;
  value: number;
}

interface TimeseriesChartProps {
  primaryName: string;
  secondaryName?: string;
  primaryYLabel?: string;
  secondaryYLabel?: string;
  primaryData: unknown[][];
  secondaryData?: unknown[][];
  primaryColors?: string[];
  secondaryColors?: string[];
  columnDefs: string[];
  baseLines?: LabelAndValue[];
  timeAxisTickCount?: number;
}

const TimeseriesChart: React.FC<TimeseriesChartProps> = ({
  primaryName,
  secondaryName = '',
  primaryYLabel = '',
  secondaryYLabel = '',
  primaryData,
  secondaryData = [],
  primaryColors = [],
  secondaryColors = [],
  columnDefs,
  baseLines = [],
  timeAxisTickCount = 0,
}: TimeseriesChartProps) => {
  const [columns, setColumns] = useState<string[]>(columnDefs);

  // Chart details
  const [primaryChartData, setPrimaryChartData] = useState<TimeSeries>();
  const [secondaryChartData, setSecondaryChartData] = useState<TimeSeries>();
  const [primaryChartMax, setPrimaryChartMax] = useState<number>();
  const [primaryChartMin, setPrimaryChartMin] = useState<number>();
  const [secondaryChartMax, setSecondaryChartMax] = useState<number>();
  const [secondaryChartMin, setSecondaryChartMin] = useState<number>();
  const [timeRange, setTimeRange] = useState<TimeRange>();

  // Tracker/tooltip details
  const [tracker, setTracker] = useState<Date>();
  const [trackerX, setTrackerX] = useState<number>();
  const [primaryTrackerDetailsInfo, setPrimaryTrackerDetailsInfo] = useState<LabelAndValue[]>([]);
  const [secondaryTrackerDetailsInfo, setSecondaryTrackerDetailsInfo] = useState<LabelAndValue[]>([]);
  const [mouseIn, setMouseIn] = useState<boolean>(false);

  // LineChart scale type
  const [isLogScale, setIsLogScale] = useState<boolean>(false);
  const [scaleType, setScaleType] = useState<string>('linear');

  useEffect(() => {
    setColumns(columnDefs);
  }, [columnDefs]);

  const resetAllChartData = () => {
    setPrimaryChartData(undefined);
    setSecondaryChartData(undefined);
    setTimeRange(undefined);
    setPrimaryChartMin(undefined);
    setPrimaryChartMax(undefined);
    setSecondaryChartMin(undefined);
    setSecondaryChartMax(undefined);
  };

  useEffect(() => {
    setScaleType(isLogScale ? 'log' : 'linear');
    resetAllChartData();
  }, [isLogScale]);

  useEffect(() => {
    setPrimaryChartData(createLineChartData(primaryName, columnDefs, primaryData, scaleType === 'log'));
  }, [primaryName, scaleType, columnDefs, primaryData]);

  useEffect(() => {
    if (secondaryData && secondaryData.length > 0) {
      setSecondaryChartData(createLineChartData(secondaryName, columnDefs, secondaryData, scaleType === 'log'));
    }
  }, [secondaryName, scaleType, columnDefs, secondaryData]);

  useEffect(() => {
    if (typeof primaryChartData !== 'undefined' && typeof secondaryChartData !== 'undefined') {
      setTimeRange(primaryChartData.timerange().extents(secondaryChartData.timerange()));
    } else if (typeof primaryChartData !== 'undefined') {
      setTimeRange(primaryChartData.timerange());
    } else if (typeof secondaryChartData !== 'undefined') {
      setTimeRange(secondaryChartData.timerange());
    }
  }, [primaryChartData, secondaryChartData]);

  useEffect(() => {
    if (typeof primaryChartData !== 'undefined' && typeof timeRange !== 'undefined') {
      const { min, max } = getChartMinMax(primaryChartData, timeRange, scaleType === 'log', columnDefs);
      setPrimaryChartMin(min);
      setPrimaryChartMax(max);
    }
  }, [primaryChartData, scaleType, timeRange, columnDefs]);

  useEffect(() => {
    if (typeof secondaryChartData !== 'undefined' && typeof timeRange !== 'undefined') {
      const { min, max } = getChartMinMax(secondaryChartData, timeRange, scaleType === 'log', columnDefs);
      setSecondaryChartMin(min);
      setSecondaryChartMax(max);
    }
  }, [secondaryChartData, scaleType, timeRange, columnDefs]);

  useEffect(() => {
    setPrimaryTrackerDetailsInfo(getTrackerDetails(tracker, primaryChartData));
  }, [tracker, primaryChartData]);

  useEffect(() => {
    setSecondaryTrackerDetailsInfo(getTrackerDetails(tracker, secondaryChartData));
  }, [tracker, secondaryChartData]);

  const getLegendCategories = () => {
    const chartData = [{ name: primaryName, data: primaryChartData }];
    if (typeof secondaryChartData !== 'undefined') {
      chartData.push({ name: secondaryName, data: secondaryChartData });
    }

    return chartData.map(({ name, data }) => {
      if (typeof data !== 'undefined') {
        return {
          key: name,
          label: `${name}`,
          value: `Med: ${round(
            data.median('value', (values: number[]) => values),
            2
          )} | Max: ${round(data.max('value'), 0)} | Min: ${round(
            data.min('value', (values: number[]) => values),
            2
          )}`,
        };
      }
      return {
        key: name,
        label: `${name}`,
        value: `Med: N/A | Max: N/A | Min: N/A`,
      };
    });
  };

  const getLineChartStyles = (colors: string[]): Record<string, unknown> => {
    const lineChartStyles: Record<string, unknown> = {};
    colors.forEach((currentColor, i) => {
      if (currentColor) {
        const currentColumn = columns[i];
        lineChartStyles[currentColumn] = {
          normal: {
            fill: 'none',
            width: '3',
            stroke: currentColor,
            opacity: 1,
          },
          highlighted: {
            fill: 'none',
            width: '1',
            stroke: currentColor,
            opacity: 1.0,
          },
          selected: {
            fill: 'none',
            width: '1',
            stroke: currentColor,
            strokeWidth: 3,
            opacity: 1.0,
          },
          muted: {
            fill: 'none',
            width: '1',
            opacity: 0.4,
            stroke: currentColor,
          },
        };
      }
    });
    return lineChartStyles;
  };
  const primaryLineChartStyles = getLineChartStyles(primaryColors);
  const secondaryLineChartStyles = getLineChartStyles(secondaryColors);

  const getScatterStyle = (col: unknown, event: unknown, colors: string[]): Record<string, unknown> => {
    const color = colors[1];
    return {
      normal: {
        fill: color,
        opacity: 0.8,
      },
      highlighted: {
        fill: color,
        opacity: 1.0,
      },
      selected: {
        fill: color,
        opacity: 1.0,
      },
      muted: {
        fill: color,
        opacity: 0.4,
      },
    };
  };

  const legendStyle = styler([
    { key: primaryName, color: primaryColors[1], width: 3 },
    { key: secondaryName, color: secondaryColors[1], width: 3 },
  ]);

  const styles = useStyles();
  return (
    <div className={styles.chartContainer}>
      <div>
        <div className={styles.iconsRow}>
          <FormControlLabel
            control={
              <Switch
                checked={isLogScale}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setIsLogScale(event.target.checked)}
                color="default"
                size="small"
              />
            }
            label={isLogScale ? 'log' : 'linear'}
          />
        </div>
        <div className={styles.legendRow}>
          <span>
            {typeof primaryChartData !== 'undefined' && (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              <Legend type="line" style={legendStyle} categories={getLegendCategories()} />
            )}
          </span>
        </div>
        {typeof primaryChartData !== 'undefined' && typeof timeRange !== 'undefined' && (
          <div>
            {trackerX && tracker && (
              <TimeseriesChartTooltip
                xPosition={trackerX}
                tracker={tracker}
                primaryName={primaryName}
                primaryChartData={primaryChartData}
                secondaryName={secondaryName}
                secondaryChartData={secondaryChartData}
              />
            )}
            <Resizable
              onMouseEnter={() => {
                setMouseIn(true);
              }}
              onMouseLeave={() => {
                setMouseIn(false);
              }}
            >
              <ChartContainer
                timeRange={timeRange}
                onTimeRangeChanged={setTimeRange}
                enableDragZoom
                trackerPosition={tracker}
                onTrackerChanged={(newTracker: Date | null, scale: (arg0: Date) => number) => {
                  if (newTracker) {
                    setTracker(new Date(newTracker));
                    setTrackerX(scale(newTracker));
                  } else {
                    setTracker(undefined);
                    setTrackerX(undefined);
                  }
                }}
                timeAxisTickCount={timeAxisTickCount}
              >
                <ChartRow height="125">
                  {typeof primaryChartMin !== 'undefined' && typeof primaryChartMax !== 'undefined' && (
                    <YAxis
                      id={primaryName}
                      label={primaryYLabel}
                      min={primaryChartMin}
                      max={primaryChartMax}
                      width={primaryYLabel ? 69 : 50}
                      labelOffset={primaryYLabel ? -17 : 0}
                      type={scaleType}
                      format={roundAndExponent}
                    />
                  )}
                  <Charts>
                    <LineChart
                      key={primaryName}
                      axis={primaryName}
                      series={primaryChartData}
                      columns={[columns[1]]}
                      style={primaryLineChartStyles}
                    />
                    <ScatterChart
                      visible={mouseIn}
                      radius={2.5}
                      axis={primaryName}
                      series={primaryChartData}
                      columns={[columns[1]]}
                      style={(col: unknown, event: unknown) => getScatterStyle(col, event, primaryColors)}
                    />
                    <LineChart
                      key={secondaryName}
                      visible={typeof secondaryChartData !== 'undefined'}
                      axis={secondaryName}
                      series={typeof secondaryChartData !== 'undefined' ? secondaryChartData : null}
                      columns={[columns[1]]}
                      style={secondaryLineChartStyles}
                    />
                    <ScatterChart
                      visible={typeof secondaryChartData !== 'undefined' && mouseIn}
                      radius={2.5}
                      axis={secondaryName}
                      series={typeof secondaryChartData !== 'undefined' ? secondaryChartData : null}
                      columns={[columns[1]]}
                      style={(col: unknown, event: unknown) => getScatterStyle(col, event, secondaryColors)}
                    />
                    {baseLines.map(({ value, label }) => (
                      <Baseline key={value} axis={primaryName} value={value} label={label} position="right" />
                    ))}
                  </Charts>
                  {typeof secondaryChartMin !== 'undefined' && typeof secondaryChartMax !== 'undefined' && (
                    <YAxis
                      id={secondaryName}
                      label={secondaryYLabel}
                      min={secondaryChartMin}
                      max={secondaryChartMax}
                      width={secondaryYLabel ? 69 : 50}
                      labelOffset={secondaryYLabel ? 17 : 0}
                      type={scaleType}
                      format={roundAndExponent}
                    />
                  )}
                </ChartRow>
              </ChartContainer>
            </Resizable>
          </div>
        )}
      </div>
      <div className={styles.trackerDetailsInfo}>
        {renderTracker(columnDefs, primaryYLabel, primaryTrackerDetailsInfo)}
      </div>
      <div className={styles.trackerDetailsInfo}>
        {secondaryData &&
          secondaryData.length > 0 &&
          renderTracker(columnDefs, secondaryYLabel, secondaryTrackerDetailsInfo)}
      </div>
    </div>
  );
};

export default TimeseriesChart;
